import { Link } from "react-router-dom";
import { modules } from "@/data/modules";
import { CheckCircle2, ArrowRight } from "lucide-react";

export default function Features() {
  return (
    <div data-testid="page-features">
      <section className="py-20 md:py-28 hero-grid-bg">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#00A651] mb-4">Product</div>
          <h1 className="text-4xl md:text-6xl font-extrabold tracking-tighter text-[#1F1E4E] leading-[1.05]">
            Every module your HR team needs. In one place.
          </h1>
          <p className="mt-6 text-lg text-slate-600 leading-relaxed">
            Explore the modules that power ClockedIn HRM — from attendance and payroll to self-service and analytics.
          </p>
          <div className="mt-8 flex justify-center gap-3 flex-wrap">
            <Link to="/contact?intent=book_demo" className="bg-[#00A651] hover:bg-[#008A43] text-white font-bold rounded-lg px-6 py-3" data-testid="features-book-demo">Book a Demo</Link>
            <Link to="/pricing" className="border-2 border-[#1F1E4E] text-[#1F1E4E] font-bold rounded-lg px-6 py-3">See Pricing</Link>
          </div>
        </div>
      </section>

      {modules.map((m, i) => {
        const I = m.icon;
        const reverse = i % 2 === 1;
        return (
          <section key={m.slug} className={`py-20 ${i % 2 === 1 ? "bg-white" : "bg-[#F8FAFC]"}`}>
            <div className="max-w-7xl mx-auto px-6">
              <div className={`grid lg:grid-cols-12 gap-12 items-start ${reverse ? "lg:flex-row-reverse" : ""}`}>
                <div className={`lg:col-span-5 ${reverse ? "lg:order-2" : ""}`}>
                  <div className="h-12 w-12 rounded-xl bg-[#E6F6EC] flex items-center justify-center mb-5">
                    <I className="h-6 w-6 text-[#00A651]" />
                  </div>
                  <h2 className="text-3xl md:text-4xl font-extrabold tracking-tighter text-[#1F1E4E]">{m.name}</h2>
                  <p className="mt-3 text-lg font-medium text-[#00A651]">{m.tagline}</p>
                  <p className="mt-6 text-sm font-bold uppercase tracking-widest text-slate-400">Business problem</p>
                  <p className="text-slate-600 mt-2 leading-relaxed">{m.problem}</p>
                  <p className="mt-5 text-sm font-bold uppercase tracking-widest text-slate-400">The solution</p>
                  <p className="text-slate-600 mt-2 leading-relaxed">{m.solution}</p>
                  <div className="mt-8 flex gap-3 flex-wrap">
                    <Link to={`/software/${m.slug}`} className="text-sm font-bold text-[#00A651] hover:underline inline-flex items-center gap-1" data-testid={`features-learn-${m.slug}`}>
                      Learn more <ArrowRight className="h-4 w-4" />
                    </Link>
                    <Link to="/contact?intent=book_demo" className="text-sm font-bold text-[#1F1E4E] hover:underline">Book a demo</Link>
                  </div>
                </div>
                <div className={`lg:col-span-7 ${reverse ? "lg:order-1" : ""} space-y-6`}>
                  <div className="bg-white rounded-2xl border border-slate-200 p-7">
                    <div className="text-xs font-bold uppercase tracking-widest text-[#00A651] mb-4">Key Benefits</div>
                    <div className="grid sm:grid-cols-2 gap-3">
                      {m.benefits.map((b, j) => (
                        <div key={j} className="flex gap-2">
                          <CheckCircle2 className="h-4.5 w-4.5 text-[#00A651] shrink-0 mt-0.5" />
                          <span className="text-sm text-slate-700">{b}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="bg-[#1F1E4E] rounded-2xl p-7 text-white">
                    <div className="text-xs font-bold uppercase tracking-widest text-[#00A651] mb-4">Detailed Features</div>
                    <div className="grid sm:grid-cols-2 gap-2 text-sm text-white/85">
                      {m.features.map((f, j) => (
                        <div key={j} className="flex gap-2 items-start">
                          <span className="h-1.5 w-1.5 rounded-full bg-[#00A651] mt-1.5 shrink-0" /> {f}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        );
      })}

      <section className="py-16 bg-white border-t border-slate-200">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h3 className="text-2xl md:text-3xl font-extrabold tracking-tight text-[#1F1E4E]">Have a workflow we don't cover yet?</h3>
          <p className="mt-3 text-slate-600">We build custom modules. Talk to our team.</p>
          <Link to="/custom-development" className="inline-flex mt-6 bg-[#00A651] hover:bg-[#008A43] text-white font-bold rounded-lg px-6 py-3" data-testid="features-custom-cta">
            Explore Custom Development
          </Link>
        </div>
      </section>
    </div>
  );
}
